import inspect from 'object-inspect'

import {
  has, map, keys, wrap, concat,
  flatten, compact, isString, overSome, constant,
  difference, isFunction, isUndefined, isPlainObject
} from 'lodash-es'

import { getElmStr } from './get-elm-str'
import { toList, isList, isEmptyArray, safeConformsTo } from './bits'

import getEnv from './get-env'
import ExtEmbedModal from '../../extension-embed-modal'

const FAIL_DELAY = 10e3

const EXTRA = 'extra'
const PATCH = 'patch'
const REPLACER = 'replacer'
const SERVICE_PATCH = 'servicePatch'

const EXTRAS = 'extras'
const PATCHES = 'patches'
const REPLACERS = 'replacers'
const SERVICE_PATCHES = 'servicePatches'

const CMP_NAME = 'cmpName'
const DIR_NAME = 'dirName'
const SERVICE_NAME = 'serviceName'

const EMBED_MODAL = 'ggsExtensionEmbedModal'
const MOCK_BACKEND = 'ggsExtensionMockBackend'
const ADMIN_POPOUT = 'ggsExtensionAdminPopout'

const importedExts = {
  [EMBED_MODAL]: fixExt(ExtEmbedModal)
}

const exth = {
  _hasExt: hasExt,
  _getExt: getExt,
  _fixExt: fixExt,
  _extract: extract,
  _storeExt: storeExt,
  _fetchExt: fetchExt,
  _extractNames: extractNames,
  _importedExts: importedExts,

  preloadExtensions,
  getServicePatches: Modules(SERVICE_PATCHES, SERVICE_NAME),
  getExtraComponents: Modules(EXTRAS, CMP_NAME),
  getComponentPatches: Modules(PATCHES, DIR_NAME),
  getComponentReplacers: Modules(REPLACERS, DIR_NAME)
}

const exposeName = 'ggsExtensions'
const isDefined = isPlainObject(window[exposeName])
export default isDefined ? window[exposeName] : window[exposeName] = exth

function preloadExtensions($rootElm) {
  const extNames = extractNames($rootElm)
  return Promise.all(extNames.map((extName) => {
    return hasExt(extName) ? getExt(extName) : storeExt(extName)
  }))
}

function Modules(modulesField, targetNameField) {
  return function modules($rootElm) {
    const modules = flatten(map(extract($rootElm), modulesField))
    for (const entryA of modules) {
      for (const entryB of modules) {
        if (entryA === entryB) continue
        if (entryA[targetNameField] === entryB[targetNameField]) {
          throw new Error(`exth#modules: ${modulesField}/${entryA[targetNameField]} cannot be processed more than once!`)
        }
      }
    }
    return modules
  }
}

function extract($rootElm) {
  const extNames = extractNames($rootElm)
  return extNames.map((extName) => {
    if (!hasExt(extName)) {
      throw new Error(`exth#extract: ${extName} requested by ${getElmStr($rootElm)} has not been preloaded!`)
    }
    return getExt(extName)
  })
}

function extractNames($rootElm) {
  return getEnv({
    def: [],
    key: 'required-extensions',
    elm: $rootElm,
    cast: toList,
    check: isList,
    isTrustedLocation: true
  })
}

function hasExt(extName) {
  return has(importedExts, extName)
}

function getExt(extName) {
  return importedExts[extName]
}

function storeExt(extName) {
  return fetchExt(extName).then(({ default: raw }) => {
    return fixExt(raw)
  }).then((ext) => {
    importedExts[extName] = ext
  })
}

function fetchExt(extName) {
  let importPromise = null

  if (extName === EMBED_MODAL) {
    importPromise = import('../../extension-embed-modal' /* webpackChunkName: "gallery-v2.extension.embed-modal" */)
  } else if (extName === MOCK_BACKEND) {
    importPromise = import('../../extension-mock-backend' /* webpackChunkName: "gallery-v2.extension.mock-backend" */)
  } else if (extName === ADMIN_POPOUT) {
    importPromise = import('../../extension-admin-popout' /* webpackChunkName: "gallery-v2.extension.admin-popout" */)
  }

  if (importPromise) {
    return importPromise
      .catch(tapCatch(wrap(`exth#fetchExt: cannot load ${extName}, will try again in ${FAIL_DELAY}ms!`, console.error)))
      .catch(delay(fetchExt, FAIL_DELAY, extName))
  } else {
    return Promise.reject(new Error(`exth#fetchExt: ${extName} is unknown!`))
  }
}

function fixExt(raw) {
  const fieldPairs = [
    [SERVICE_PATCHES, SERVICE_PATCH],
    [EXTRAS, EXTRA],
    [PATCHES, PATCH],
    [REPLACERS, REPLACER]
  ]

  const checkPairs = [
    [SERVICE_PATCHES, { serviceName: isString, decorator: isFunction }],
    [EXTRAS, { cmpName: isString, template: isString, bindings: isPlainObject, controller: isFunction }],
    [PATCHES, { dirName: isString, controller: isFunction, template: overSome(isString, isFunction), origPlh: overSome(isString, isUndefined) }],
    [REPLACERS, { dirName: isString, template: isString, controller: isFunction }]
  ]

  const ext = {}
  for (const [fullModulesField, shortModulesField] of fieldPairs) {
    ext[fullModulesField] = compact(flatten(concat(raw[fullModulesField], raw[shortModulesField])))
  }

  for (const [modulesField, moduleSchema] of checkPairs) {
    for (const module of ext[modulesField]) {
      if (!safeConformsTo(module, moduleSchema)) {
        throw new Error(`exth#fixExt: all modules from "${modulesField}" including ${inspect(module)} should match ${inspect(moduleSchema)}!`)
      }
      if (!isEmptyArray(difference(keys(module), keys(moduleSchema)))) {
        throw new Error(`exth#fixExt: all modules from "${modulesField}" including ${inspect(module)} should not include anything but these keys: ${keys(moduleSchema)}!`)
      }
    }
  }

  return ext
}

// helpers

function tapCatch(action) {
  return function tapCatch_wrapper(err) {
    return Promise.resolve(action(err)).then(constant(Promise.reject(err)))
  }
}

function delay(action, ms, ...args) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  }).then(() => {
    return action(...args)
  })
}
